"use strict";
import { lazy, useMemo } from "react";
import { Navigate, matchPath, useLocation } from "react-router-dom";
import { isBrowserRouterEnabled } from "utils/env";
import Swap from "pages/Swap";
import { FeatureFlags } from "uniswap/src/features/gating/flags";
import { useFeatureFlag } from "uniswap/src/features/gating/hooks";
import i18n from "uniswap/src/i18n";
const CreatePosition = lazy(() => import("pages/Pool/Positions/create/CreatePosition"));
const AddLiquidityV3WithTokenRedirects = lazy(() => import("pages/AddLiquidityV3/redirects"));
const AddLiquidityV2WithTokenRedirects = lazy(() => import("pages/AddLiquidityV2/redirects"));
const RedirectExplore = lazy(() => import("pages/Explore/redirects"));
const MigrateV2 = lazy(() => import("pages/MigrateV2"));
const MigrateV2Pair = lazy(() => import("pages/MigrateV2/MigrateV2Pair"));
const MigrateV3 = lazy(() => import("pages/MigrateV3"));
const NotFound = lazy(() => import("pages/NotFound"));
const Pool = lazy(() => import("pages/Pool"));
const LegacyPoolRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.LegacyPoolRedirects }))
);
const PoolFinderRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.PoolFinderRedirects }))
);
const LegacyPositionPageRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.LegacyPositionPageRedirects }))
);
const RemoveLiquidityV2WithTokenRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.RemoveLiquidityV2WithTokenRedirects }))
);
const PositionPage = lazy(() => import("pages/Pool/Positions/PositionPage"));
const V2PositionPage = lazy(() => import("pages/Pool/Positions/V2PositionPage"));
const PoolDetails = lazy(() => import("pages/PoolDetails"));
const TokenDetails = lazy(() => import("pages/TokenDetails"));
const ExtensionPasskeyAuthPopUp = lazy(() => import("pages/ExtensionPasskeyAuthPopUp"));
const PasskeyManagement = lazy(() => import("pages/PasskeyManagement"));
const ExtensionUninstall = lazy(() => import("pages/ExtensionUninstall/ExtensionUninstall"));
export function useRouterConfig() {
  const browserRouterEnabled = isBrowserRouterEnabled();
  const { hash } = useLocation();
  const isEmbeddedWalletEnabled = useFeatureFlag(FeatureFlags.EmbeddedWallet);
  return useMemo(
    () => ({
      browserRouterEnabled,
      hash,
      isEmbeddedWalletEnabled
    }),
    [browserRouterEnabled, hash, isEmbeddedWalletEnabled]
  );
}
const StaticTitlesAndDescriptions = {
  UniswapTitle: i18n.t("title.uniswapTradeCrypto"),
  SwapTitle: i18n.t("title.buySellTradeEthereum"),
  SwapDescription: i18n.t("title.swappingMadeSimple"),
  DetailsPageBaseTitle: i18n.t("common.buyAndSell"),
  TDPDescription: i18n.t("title.realTime"),
  PDPDescription: i18n.t("title.tradeTokens"),
  MigrateTitle: i18n.t("title.migratev2"),
  MigrateTitleV3: i18n.t("title.migratev3"),
  MigrateDescription: i18n.t("title.easilyRemove"),
  MigrateDescriptionV4: i18n.t("title.easilyRemoveV4"),
  AddLiquidityDescription: i18n.t("title.earnFees"),
  PasskeyManagementTitle: i18n.t("title.managePasskeys")
};
function createRouteDefinition(route) {
  return {
    getElement: () => null,
    getTitle: () => StaticTitlesAndDescriptions.UniswapTitle,
    getDescription: () => StaticTitlesAndDescriptions.SwapDescription,
    enabled: () => true,
    path: "/",
    nestedPaths: [],
    // overwrite the defaults
    ...route
  };
}
export const routes = [
  // ────────────────────────────────────────────────────────────
  //  Accessible page
  // ────────────────────────────────────────────────────────────
  createRouteDefinition({
    path: "/swap",
    getElement: () => <Swap />,
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle,
    getDescription: () => StaticTitlesAndDescriptions.SwapDescription
  }),
  // ────────────────────────────────────────────────────────────
  //  Root (“/”) → /swap
  // ────────────────────────────────────────────────────────────
  createRouteDefinition({
    path: "/",
    getElement: () => <Navigate to="/swap" replace />,
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle,
    getDescription: () => StaticTitlesAndDescriptions.SwapDescription
  }),
  // ────────────────────────────────────────────────────────────
  //  Catch-all for everything else → /swap
  // ────────────────────────────────────────────────────────────
  createRouteDefinition({
    path: "*",
    getElement: () => <Navigate to="/swap" replace />
  })
];
export const findRouteByPath = (pathname) => {
  for (const route of routes) {
    const match = matchPath(route.path, pathname);
    if (match) {
      return route;
    }
    const subPaths = route.nestedPaths.map((nestedPath) => `${route.path}/${nestedPath}`);
    for (const subPath of subPaths) {
      const match2 = matchPath(subPath, pathname);
      if (match2) {
        return route;
      }
    }
  }
  return void 0;
};
